import { useState, useEffect } from 'react';
import { Container, Typography, Box, Button, Switch, FormControlLabel, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { baseClient } from '../api/baseClient';
import { useNavigate } from 'react-router-dom';
import type { Workflow } from '../types/Workflow';


const WorkflowsPage = () => {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const response = await baseClient.get('/workflows');
      setWorkflows((response as any).data as Workflow[]);
      setError(null);
    } catch (err) {
      console.error('Error fetching workflows:', err);
      setError('Failed to load workflows. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      await baseClient.post(`/workflows/${id}/toggle`);
      // Update the local state
      setWorkflows(workflows.map(workflow => 
        workflow.id === id ? { ...workflow, is_active: !currentStatus } : workflow
      ));
    } catch (err) {
      console.error('Error toggling workflow status:', err);
      setError('Failed to update workflow status. Please try again.');
    }
  };

  const handleDeleteClick = (id: string) => {
    setWorkflowToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!workflowToDelete) return;
    
    try {
      await baseClient.delete(`/workflows/${workflowToDelete}`);
      // Remove from local state
      setWorkflows(workflows.filter(workflow => workflow.id !== workflowToDelete));
      setError(null);
    } catch (err) {
      console.error('Error deleting workflow:', err);
      setError('Failed to delete workflow. Please try again.');
    } finally {
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    }
  };

  const handleCreateWorkflow = () => {
    navigate('/settings/workflows/create');
  };

  const handleEditWorkflow = (id: string) => {
    navigate(`/settings/workflows/edit/${id}`);
  };

  const formatTriggerType = (triggerType: string) => {
    // Convert from snake_case to readable format
    return triggerType
      .split('.')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Workflow Automation
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleCreateWorkflow}
          >
            Create Workflow
          </Button>
        </Box>

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
            <Typography>{error}</Typography>
          </Paper>
        )}

        {loading ? (
          <Typography>Loading workflows...</Typography>
        ) : workflows.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>No workflows found</Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Create your first workflow to automate your CRM processes.
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={handleCreateWorkflow}
            >
              Create Workflow
            </Button>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Trigger</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workflows.map((workflow) => (
                  <TableRow key={workflow.id}>
                    <TableCell>
                      <Typography variant="body1">{workflow.name}</Typography>
                      {workflow.description && (
                        <Typography variant="body2" color="textSecondary">
                          {workflow.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatTriggerType(workflow.trigger_type)}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={workflow.is_active}
                            onChange={() => handleToggleActive(workflow.id, workflow.is_active)}
                            color="primary"
                          />
                        }
                        label={workflow.is_active ? "Active" : "Inactive"}
                      />
                    </TableCell>
                    <TableCell>{new Date(workflow.updated_at).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <IconButton 
                        aria-label="edit" 
                        onClick={() => handleEditWorkflow(workflow.id)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        aria-label="delete" 
                        onClick={() => handleDeleteClick(workflow.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Workflow</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this workflow? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WorkflowsPage; 